import React, { useEffect, useState } from "react";
import { Layers } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import LoadFailed from "./LoadFailed.jsx";

// Where each skill is installed, and whether the next kind of buyer is within
// reach -- the Skill Universe's R5 and R6 (Backend skill_engine.py).
//
// Install states are read from real calls every time, never stored: a skill is
// "solidified" after two passing calls in a row and drops back the moment it
// slips, because the claim is about now. Promotion is a SUGGESTION with its
// reasons. View 1 is explicit that a person decides, never the system.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

const STATE_COPY = {
  not_seen: { label: "Not seen yet", tone: "var(--c-400)" },
  introduced: { label: "Introduced", tone: "var(--c-600)" },
  solidified: { label: "Solidified", tone: "var(--blue-600)" },
  perfected: { label: "Perfected", tone: "var(--green-600)" },
};

function StateChip({ state }) {
  const s = STATE_COPY[state] || STATE_COPY.not_seen;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: s.tone, border: `1px solid ${s.tone}`,
                   borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

export default function SkillProgress({ employeeId = null, isManager = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Another person (or a retry): no old streaks under the new name, no old error.
    setData(null);
    setError("");
    const query = employeeId ? `?employee=${employeeId}` : "";
    apiGet(`/RevOps/skill-progress/${query}`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load skill progress."));
    return () => { cancelled = true; };
  }, [employeeId, attempt]);

  if (error) return <LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return null;

  const states = Object.entries(data.install_states || {})
    .sort(([, a], [, b]) => (a.layer || "L9").localeCompare(b.layer || "L9"));
  const promo = data.promotion || {};

  return (
    <div style={{ ...card, marginTop: 14 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                     fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                     color: "var(--c-600)" }}>
        <Layers size={13} /> {isManager ? "Skill progress" : "Your skill progress"}
      </span>
      <p style={{ margin: "4px 0 0", fontSize: 12, ...muted }}>
        Solidified = {data.thresholds?.solidified_streak} calls in a row at {data.thresholds?.pass_at}+.
        Perfected = {data.thresholds?.perfected_streak} in a row, one of them under pressure
        (an objection, a price talk, or more than one buyer speaking). These lines are first guesses,
        still being checked against real calls.
      </p>

      {states.length === 0 ? (
        <p style={{ fontSize: 12.5, ...muted, margin: "10px 0 0" }}>No scored calls yet.</p>
      ) : (
        <div style={{ marginTop: 8 }}>
          {states.map(([key, s]) => (
            <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 10,
                                    alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--c-200)" }}>
              <span style={{ fontSize: 12.5, color: "var(--c-900)" }}>
                {s.label}
                <span style={{ ...muted, fontSize: 11.5 }}> · {s.layer} · {s.graded_calls} graded call{s.graded_calls === 1 ? "" : "s"}</span>
              </span>
              <StateChip state={s.state} />
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--c-200)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-900)" }}>
          {promo.next_tier
            ? <>Next kind of buyer: {promo.next_tier_label}</>
            : promo.current_tier ? "At the top tier" : "Buyer tier not set yet"}
        </div>
        {promo.current_tier_label && (
          <div style={{ fontSize: 12, ...muted, marginTop: 2 }}>
            Mostly selling to: {promo.current_tier_label} (last 30 days)
          </div>
        )}
        {promo.next_tier && (
          <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--c-800)" }}>
            {promo.ready_on_what_is_measured
              ? "Everything the scorecard can measure for this step is showing. A coach makes the call."
              : "Not showing yet on what the scorecard measures."}
          </p>
        )}
        {(promo.blockers || []).length > 0 && (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--c-700)" }}>
            {promo.blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        )}
        {(promo.not_measured || []).length > 0 && (
          <p style={{ margin: "6px 0 0", fontSize: 11.5, ...muted }}>
            Not measured by the scorecard yet: {promo.not_measured.join(", ")}. A coach has to judge these.
          </p>
        )}
        {isManager && (promo.needs_confirmation || []).length > 0 && (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5, ...muted }}>
            {promo.needs_confirmation.map((b) => <li key={b}>{b}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
